import React from "react";
import { RefreshCw } from "lucide-react";

import * as styles from "@styles/ClientsPage.css";
import type { OptionTs } from "@ps-bridge/interop";
import type { Ok } from "@ps-bridge/ok";
import { ok } from "@ps-bridge/ok";
import * as Option from "@utils/Option";
import type { SupplyStatusResponse } from "@services/clients/DocumentAssistantSupplyApi";

type SupplyUploadStatusViewProps = {
  readonly disabled: boolean;
  readonly selectedFileView: React.ReactElement;
  readonly statusRows: SupplyStatusResponse[];
  readonly errorView: React.ReactElement;
  readonly onSelectFile: (fileList: OptionTs<FileList>) => Promise<Ok>;
  readonly onRefreshStatus: (supplyId: string) => Promise<Ok>;
};

const fileListOption = (event: React.ChangeEvent<HTMLInputElement>): OptionTs<FileList> => {
  const files = event.target.files;
  return files ? Option.some(files) : Option.none();
};

const SupplyUploadStatusView: React.FC<SupplyUploadStatusViewProps> = ({
  disabled,
  selectedFileView,
  statusRows,
  errorView,
  onSelectFile,
  onRefreshStatus,
}) => (
  <section className={styles.supplyPanel} aria-label="Document supply upload status">
    <div className={styles.supplyHeader}>
      <div>
        <h3 className={styles.sectionTitle}>Manual supply upload</h3>
        <p className={styles.sectionSubtitle}>
          Submit a local file to the document supply pipeline and track readiness milestones.
        </p>
      </div>
      <label className={styles.supplyUploadButton}>
        <RefreshCw size={16} className={styles.actionIcon} />
        Upload
        <input
          type="file"
          className={styles.fileInput}
          disabled={disabled}
          onChange={(event) => {
            onSelectFile(fileListOption(event)).then(() => ok);
            return ok;
          }}
        />
      </label>
    </div>
    {selectedFileView}
    {errorView}
    <div className={styles.supplyStatusList}>
      {statusRows.map((row) => (
        <article key={row.supplyId} className={styles.supplyStatusRow}>
          <div>
            <p className={styles.listTitle}>{row.displayName}</p>
            <p className={styles.listMeta}>
              {row.uploadStatus} · indexing {row.indexing} · retrieval {row.retrieval}
            </p>
            <p className={styles.supplyTrace}>Trace: {row.trace.traceId}</p>
          </div>
          <button
            type="button"
            className={styles.ghostButton}
            onClick={() => {
              onRefreshStatus(row.supplyId).then(() => ok);
              return ok;
            }}
          >
            <RefreshCw size={16} className={styles.actionIcon} />
            Refresh
          </button>
        </article>
      ))}
    </div>
  </section>
);

export default SupplyUploadStatusView;
